import { BaseFeature } from '../../core/base-feature.js';

/**
 * ScreenRecorderFeature - Frontend feature for DVR-style screen history playback
 *
 * Communicates with the desktop screen-recorder MainProcessFeature to:
 * - Request frame index (metadata) for time ranges
 * - Request individual or batched frame JPEG data
 * - Request recording status
 * - Request activity density maps for scrubber heatmap
 * - Trigger burst capture mode
 */
export class ScreenRecorderFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.pendingRequests = new Map();
        this.requestTimeout = 30000;
    }

    static getMetadata() {
        return {
            id: 'screen-recorder',
            name: 'Screen Recorder',
            version: '1.0',
            capabilities: [
                'get_frame_index',
                'get_frame',
                'get_frames_batch',
                'get_status',
                'get_activity_map',
                'trigger_burst'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[ScreenRecorder] Initialized');
    }

    async handleMessage(message) {
        const { action, payload } = message;

        // Check for pending request resolution
        if (payload && payload.originalMessageId) {
            const resolver = this.pendingRequests.get(payload.originalMessageId);
            if (resolver) {
                this.pendingRequests.delete(payload.originalMessageId);
                if (payload.error) {
                    resolver.reject(new Error(payload.error));
                } else {
                    resolver.resolve(payload);
                }
                return;
            }
        }

        // Handle response actions by stripping _response suffix
        const baseAction = action.replace(/_response$/, '');
        // Check if any pending request matches this action
        for (const [msgId, resolver] of this.pendingRequests) {
            if (resolver.action === baseAction) {
                this.pendingRequests.delete(msgId);
                if (payload?.error) {
                    resolver.reject(new Error(payload.error));
                } else {
                    resolver.resolve(payload);
                }
                return;
            }
        }
    }

    // ==================== Public API ====================

    /**
     * Request frame index (metadata only, no BLOBs) for a time range
     * @param {number} since - Start timestamp
     * @param {number} until - End timestamp
     * @param {number} limit - Max frames to return
     * @returns {Promise<{frames: Array}>}
     */
    async requestFrameIndex(since, until, limit = 1000) {
        return this._sendRequest('get_frame_index', { since, until, limit });
    }

    /**
     * Request a single frame's base64 JPEG data
     * @param {string} eventId - Sacred Timeline event ID
     * @returns {Promise<{imageData: string, mimeType: string}>}
     */
    async requestFrame(eventId) {
        return this._sendRequest('get_frame', { eventId });
    }

    /**
     * Request multiple frames for pre-fetching
     * @param {string[]} eventIds - Array of event IDs
     * @returns {Promise<{frames: Object}>}
     */
    async requestFramesBatch(eventIds) {
        return this._sendRequest('get_frames_batch', { eventIds });
    }

    /**
     * Request recording status
     * @returns {Promise<{isRecording, frameCount, currentInterval, isIdle, isBursting}>}
     */
    async requestStatus() {
        return this._sendRequest('get_status', {});
    }

    /**
     * Request per-minute activity density for scrubber heatmap
     * @param {number} since - Start timestamp
     * @param {number} until - End timestamp
     * @returns {Promise<{minutes: Array}>}
     */
    async requestActivityMap(since, until) {
        return this._sendRequest('get_activity_map', { since, until });
    }

    /**
     * Trigger burst capture mode (1s intervals)
     * @param {number} durationMs - Burst duration in milliseconds
     */
    async triggerBurst(durationMs = 10000) {
        return this._sendRequest('trigger_burst', { durationMs });
    }

    // ==================== Communication ====================

    _sendRequest(action, payload) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);

            const resolver = { resolve, reject, action };
            this.pendingRequests.set(messageId, resolver);

            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error(`Timeout waiting for ${action} response`));
                }
            }, this.requestTimeout);
        });
    }

    async cleanup() {
        console.log('[ScreenRecorder] Cleaning up...');
        this.pendingRequests.clear();
    }
}
